import { useState } from "react";
import type { UseQueryResult } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { useLocalization } from "@/context/localization-context";
import { Ago, EmptyBlock, FetchingDot, SectionCard } from "../../components/primitives";
import { formatClock, formatStamp } from "../../format";
import type { LiveSection } from "../../types";
import { Duration, MethodChip, QueryBody, StatusChip } from "./bits";

const PAGE = 10;

/** The slowest recent requests of this instance (>= 2 s, slow-by-design routes excluded), newest first. */
export function SlowRequests({ live }: { live: UseQueryResult<LiveSection> }) {
  const { t } = useLocalization();
  const [showAll, setShowAll] = useState(false);
  return (
    <SectionCard
      title={t("monitoring.requests.slow.title")}
      description={t("monitoring.requests.slow.description")}
      actions={<FetchingDot active={live.isFetching} />}
    >
      <QueryBody query={live} height={120}>
        {(data) => {
          const slow = data.http.slow;
          if (!slow.length) return <EmptyBlock>{t("monitoring.requests.slow.empty")}</EmptyBlock>;
          const shown = showAll ? slow : slow.slice(0, PAGE);
          return (
            <>
              <ul className="divide-y divide-border/60">
                {shown.map((entry) => (
                  <li key={`${entry.ts}-${entry.method}-${entry.path}-${entry.ms}`} className="flex flex-wrap items-center gap-x-3 gap-y-1 py-2">
                    <time
                      dateTime={new Date(entry.ts).toISOString()}
                      title={formatStamp(entry.ts)}
                      className="w-[4.5rem] shrink-0 font-mono text-[11px] tabular-nums text-muted-foreground"
                    >
                      {formatClock(entry.ts)}
                    </time>
                    <div className="flex min-w-0 flex-1 basis-[14rem] items-center gap-1.5">
                      <MethodChip method={entry.method} />
                      <span className="min-w-0 break-all font-mono text-[11px]" title={entry.route}>{entry.path}</span>
                    </div>
                    <div className="flex shrink-0 items-center gap-3">
                      <StatusChip status={entry.status} />
                      <Duration ms={entry.ms} route={entry.route} className="w-16 text-right text-xs" />
                      <Ago ts={entry.ts} className="hidden w-24 text-right text-[11px] text-muted-foreground sm:inline" />
                    </div>
                  </li>
                ))}
              </ul>
              {slow.length > PAGE && (
                <div className="mt-2 flex justify-center">
                  <Button variant="ghost" size="sm" className="text-xs" onClick={() => setShowAll(!showAll)} aria-expanded={showAll}>
                    {showAll
                      ? t("monitoring.requests.routes.showFewer", { count: PAGE })
                      : t("monitoring.requests.slow.showAll", { count: slow.length })}
                  </Button>
                </div>
              )}
            </>
          );
        }}
      </QueryBody>
    </SectionCard>
  );
}
